import { photoBucket, supabase } from '../data/supabase';
import { readErrorText } from '../data/cloudContent';
import { prepareImageUpload, type PrepareOptions } from './imageUpload';

export interface UploadedPhoto {
  url?: string;
  error?: string;
}

/**
 * Photos now live in Supabase storage rather than in browser storage, so they
 * appear on every device and are not capped by the browser's 5 MB limit. They
 * are still resized first — a smaller file loads faster for fans on mobile.
 */
export async function uploadPhoto(file: File, options: PrepareOptions = {}): Promise<UploadedPhoto> {
  const prepared = await prepareImageUpload(file, { maxDimension: 1800, quality: 0.82, targetBytes: 900_000, ...options });
  if (!prepared.dataUrl) return { error: prepared.error ?? 'Could not read that image.' };
  return uploadPhotoDataUrl(prepared.dataUrl);
}

/** Uploads an already-prepared image and returns its public URL. */
export async function uploadPhotoDataUrl(dataUrl: string): Promise<UploadedPhoto> {
  try {
    const blob = dataUrlToBlob(dataUrl);
    const path = `${new Date().getFullYear()}/${crypto.randomUUID()}.jpg`;
    const { error } = await supabase.storage.from(photoBucket).upload(path, blob, {
      contentType: blob.type || 'image/jpeg',
      cacheControl: '31536000',
      upsert: false,
    });
    if (error) throw error;

    const { data } = supabase.storage.from(photoBucket).getPublicUrl(path);
    if (!data?.publicUrl) return { error: 'Uploaded, but could not read the photo address back.' };
    return { url: data.publicUrl };
  } catch (error) {
    const raw = readErrorText(error);
    if (/row-level security|policy|denied|permission|JWT|401|403/i.test(raw)) {
      return { error: 'Sign in with your emailed link before uploading photos — uploads need a signed-in owner.' };
    }
    if (/bucket|not found|404/i.test(raw)) {
      return { error: `Photo storage is not set up. Re-run the setup script in Supabase. (${raw})` };
    }
    if (/failed to fetch|network/i.test(raw)) {
      return { error: 'No connection — photo upload needs to reach the cloud.' };
    }
    return { error: raw ? `Upload failed: ${raw}` : 'Could not upload that photo.' };
  }
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, encoded] = dataUrl.split(',');
  const type = /:(.*?);/.exec(header)?.[1] ?? 'image/jpeg';
  const binary = atob(encoded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes], { type });
}
